import { ConstantQueryParamValues, KeyItem, MatchUpTypeEnum } from 'types/global'
import { getWebRootUrl } from './api'
import { GameType } from 'components/chart/ResultsDoughnutChart/ResultsDoughnutChartTypes'
import { MyAccountSections } from './my-account'

export const PLAYERS_FETCH_SIZE_ANONYMOUS = 10
export const PLAYERS_FETCH_SIZE_LOGGED_IN = 50
export const LANG = 'eng'

export const REL_PATHS = {
  PLAYER_PROFILE: '/player-profile',
  PLAYER_SEARCH: '/player-search',
  MY_ACCOUNT: '/my-account',
  WATCHLISTS: '/watchlists',
  PSQ: '/rate-my-game',
  DISCOVER: '/your-number',
  NEWS: '/news',
  OUR_PARTNERS: '/our-partners',
  CONTACT: '/contact',
  FAQS: '/faq',
  PRIVACY_POLICY: '/privacy-policy',
  TERMS_AND_CONDITIONS: '/terms-and-conditions',
  SYSTEM_RULES: '/system-rules',
  FASTTRACK_REG: '/registration',
  WALKUP_REG: '/register',
  ACTIVATION: '/registration?activate-account=true',
  ERROR: '/error',
} as const

export type WtnPage = keyof typeof REL_PATHS

// TODO: currently hardcoded, implement logic to get user's preferred language when we add internationalization
export const getCurrentLang = () => LANG

type QueryParams = { [key: string]: string | ConstantQueryParamValues }

interface PathOptions {
  queryParams?: QueryParams
  uriFragment?: string
}

export const createQueryString = ({ queryParams, uriFragment }: PathOptions) => {
  let queryString = ''
  if (queryParams) {
    for (let key in queryParams) {
      if (queryString) queryString = queryString + '&'
      queryString = queryString + key + '=' + queryParams[key]
    }
    queryString = '?' + queryString
  }

  if (uriFragment) queryString = `${queryString}#${uriFragment}`

  return queryString
}

export const createRelPath = (page: WtnPage, options: PathOptions = {}) => {
  let queryString = createQueryString(options)
  return `/${getCurrentLang()}${REL_PATHS[page]}${queryString}`
}

export const createFullUrl = (page: WtnPage, options?: PathOptions) =>
  getWebRootUrl() + createRelPath(page, options)

// HREFs
export const NOT_FOUND_HREF = '/404'
export const MY_ACCOUNT_FULL_URL = createFullUrl('MY_ACCOUNT')
export const MY_ACCOUNT_HREF = createRelPath('MY_ACCOUNT')
export const MY_DETAILS_HREF = createRelPath('MY_ACCOUNT', {
  queryParams: { section: MyAccountSections.MY_DETAILS },
})
export const SIGN_IN_DETAILS_HREF = createRelPath('MY_ACCOUNT', {
  queryParams: { section: MyAccountSections.SIGN_IN_DETAILS },
})
export const PRIVACY_AND_CONSENTS_HREF = createRelPath('MY_ACCOUNT', {
  queryParams: { section: MyAccountSections.PRIVACY_AND_CONSENTS },
})
export const PSQ_HREF = createRelPath('PSQ')
export const PRIVACY_POLICY_HREF = createRelPath('PRIVACY_POLICY')
export const TERMS_AND_CONDITIONS_HREF = createRelPath('TERMS_AND_CONDITIONS')
export const CONTACT_US_HREF = createRelPath('CONTACT')
export const CONTACT_US_FULL_URL = createFullUrl('CONTACT')
export const ACTIVATION_PAGE_BASE_URL = createFullUrl('ACTIVATION')
export const WATCHLISTS_HREF = createRelPath('WATCHLISTS')
export const ERROR_PAGE_FULL_URL = createFullUrl('ERROR')
export const WALK_UP_REG_HREF = createRelPath('WALKUP_REG')
export const WALK_UP_REG_FULL_URL = createFullUrl('WALKUP_REG')
export const FAQS_MY_MATCHES_HREF = createRelPath('FAQS', { uriFragment: 'my-matches' })
export const PLAYER_SEARCH_HREF = createRelPath('PLAYER_SEARCH')
export const POST_SCORE_HREF = createRelPath('PLAYER_PROFILE', { 
  queryParams: {
    'own-profile': 'true',
  },
  uriFragment: 'post-score' 
})

export const WTN_SCALE_LOW = 40
export const WTN_SCALE_HIGH = 1
export const HIGH_CONFIDENCE_THRESHOLD = 60
export const MOBILE_BREAKPOINT = 576
export const TABLET_BREAKPOINT = 768
export const LG_TABLET_BREAKPOINT = 832
export const DESKTOP_BREAKPOINT = 992
export const LG_DESKTOP_BREAKPOINT = 1100
export const MIN_SEARCH_CHARS = 3
export const LS_COUNTRIES_KEY_BASE = 'allCountries'
/* Change version when the list of countries gets updated so that the outdated versions can be cleared from localStorage */
const LS_COUNTRIES_KEY_CURRENT_VERSION = 1.4
export const LS_COUNTRIES_KEY = `${LS_COUNTRIES_KEY_BASE}.v${LS_COUNTRIES_KEY_CURRENT_VERSION}`

export const matchTypes: KeyItem<GameType>[] = [
  { key: MatchUpTypeEnum.SINGLES, label: 'Singles' },
  { key: MatchUpTypeEnum.DOUBLES, label: 'Doubles' },
  { key: 'BOTH', label: 'Both' },
]

export enum LocalStorageVars {
  AUTH_TOKEN = 'authToken',
  TENNIS_ID = 'tennisId',
  COMPLETED_TOURS = 'completedTours',
  REG_DATA = 'wtnRegistration',
  REG_LOG = 'wtnRegLog',
  SAVED_USER_INFO = 'myWtnSaved',
  USER = 'currentUser',
  PUBLIC_RMG_ANSWERS = 'publicRmgAnswers',
  REG_SEED = 'wtnRegistrationSeed',
  HIDE_PROFILE_STRENGTH = 'hideProfileStrength',
}

export enum SessionStorageVars {
  DESTINATION_WATCHLISTS = 'destinationWatchlistIds',
}

export const LocalStorageAlerts = {
  ALERT_LOGOUT: 'userLogoutAlert',
  ALERT_LOGIN: 'userLoginAlert',
} as const

export interface RegistrationData {
  commConsentItf: boolean
  commConsentNa: boolean
  termsAndConditions: string
  isWalkUpUser: boolean
  parentFullName?: string
  parentEmailAddress?: string
}

export interface LogEntry {
  start: string
  end?: string
  operation: string
  error?: null | string
  rayId?: null | string
  ctx: string | null
}

export type RegistrationLog = { refs: string[]; logs: Record<string, LogEntry> }
